import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../../../utils";
import AuthenticatedView from "./AuthenticatedView";
import GuestView from "./GuestView";
import { debounce } from "./utils";
import { MainContainer } from "./styles";

const HomeContentSection = ({ authTheme = "night", onToggleAuthTheme }) => {
  const {
    isAuthenticated,
    authToken,
    fetchTodoNoteData,
    sendTodoNoteData,
    jumpTodoHistory,
    fetchAccountingData,
    sendAccountingData,
    fetchAllWorkHours,
    deleteWorkHourRecord,
  } = useAuth();

  const [noteData, setNoteData] = useState("");
  const [todoData, setTodoData] = useState([]);
  const activeSession = useRef(authToken);
  const sessionSequence = useRef(0);
  if (activeSession.current !== authToken) sessionSequence.current += 1;
  activeSession.current = authToken;
  useEffect(() => {
    activeSession.current = authToken;
    return () => { activeSession.current = null; };
  }, [authToken]);
  const [accountingOwner, setAccountingOwner] = useState(authToken);
  const onAccountingSaved = snapshot => {
    if (activeSession.current !== authToken) return;
    setAccountingOwner(authToken);
    setAccountingData(snapshot);
  };
  const [accountingData, setAccountingData] = useState(null);
  const [accountingError, setAccountingError] = useState("");
  const retryAccounting = async () => {
    setAccountingOwner(authToken);
    setAccountingError("");
    setAccountingData(null);
    try {
      const result = await fetchAccountingData();
      if (activeSession.current !== authToken) return;
      if (!result.success) throw new Error(result.error);
      setAccountingData({ debt: result.data.debt || [], payments: result.data.payments || [] });
    } catch { if (activeSession.current === authToken) setAccountingError("帳單讀取失敗"); }
  };
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 600);

  const location = useLocation();
  const navigate = useNavigate();

  /* ---------- Resize Handling ---------- */
  useEffect(() => {
    const handleResize = debounce(() => setIsSmallScreen(window.innerWidth <= 600), 300);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- Fetch Data after Login ---------- */
  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      if (!isAuthenticated) { setAccountingData(null); setAccountingError(""); return; }
      setAccountingOwner(authToken);
      setAccountingData(null);
      setAccountingError("");
      try {
        const [todoNoteData, accountingResp] = await Promise.all([
          fetchTodoNoteData(),
          fetchAccountingData(),
        ]);
        if (cancelled || activeSession.current !== authToken) return;
        if (todoNoteData.success) {
          setNoteData(todoNoteData.data.note_data || "");
          setTodoData(todoNoteData.data.todo_data || []);
        }
        if (accountingResp.success) {
          setAccountingError("");
          setAccountingData({
            debt: accountingResp.data.debt || [],
            payments: accountingResp.data.payments || [],
          });
        } else { setAccountingError("帳單讀取失敗"); }
      } catch (err) {
        if (cancelled || activeSession.current !== authToken) return;
        setAccountingError("帳單讀取失敗");
        console.error("Data fetch failed:", err);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [isAuthenticated, authToken]);

  /* ---------- Submit Notes ---------- */
  const handleDataSubmit = async () => {
    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your data.",
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => Swal.showLoading(),
      });
      await sendTodoNoteData(noteData, todoData);
      Swal.close();
      Swal.fire({ icon: "success", title: "Success!", text: "Data submitted successfully!" });
    } catch (err) {
      Swal.close();
      Swal.fire({ icon: "error", title: "Oops...", text: "Failed to submit data." });
    }
  };

  const handleTodoHistoryJump = async (direction) => {
    const isRedo = direction === "redo";
    try {
      Swal.fire({
        title: isRedo ? "往後跳..." : "往前跳...",
        text: "正在切換版本。",
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => Swal.showLoading(),
      });

      const result = await jumpTodoHistory(direction);
      Swal.close();

      if (result.success) {
        setNoteData(result.data.note_data || "");
        setTodoData(result.data.todo_data || []);
        Swal.fire({
          icon: "success",
          title: isRedo ? "已往後跳" : "已往前跳",
          text: result.message || "版本已切換。",
          confirmButtonColor: "#ff9800",
        });
        return;
      }

      Swal.fire({
        icon: result.status === 409 ? "info" : "error",
        title: isRedo ? "不能再往後跳" : "不能再往前跳",
        text: result.error || "沒有可切換的版本。",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "版本切換失敗",
        text: "請稍後再試。",
      });
    }
  };

  return (
    <MainContainer>
      {isAuthenticated ? (
        <AuthenticatedView
          key={sessionSequence.current}
          noteData={noteData}
          todoData={todoData}
          accountingData={accountingOwner === authToken ? accountingData : null}
          accountingError={accountingOwner === authToken ? accountingError : ""}
          retryAccounting={retryAccounting}
          onAccountingSaved={onAccountingSaved}
          setNoteData={setNoteData}
          setTodoData={setTodoData}
          sendAccountingData={sendAccountingData}
          fetchAllWorkHours={fetchAllWorkHours}
          deleteWorkHourRecord={deleteWorkHourRecord}
          onSubmit={handleDataSubmit}
          onHistoryJump={handleTodoHistoryJump}
          authTheme={authTheme}
          onToggleAuthTheme={onToggleAuthTheme}
          isSmallScreen={isSmallScreen}
          location={location}
          navigate={navigate}
        />
      ) : (
        <GuestView />
      )}
    </MainContainer>
  );
};

export default HomeContentSection;

